import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { createSelectionColumn } from '@/components/ui/table/selection-column';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { getStatusSortValue } from '@/modules/collections/utils/statusSorting';
import { DatasourceType } from '@/shared/types/collections';
import { COLUMN_PRESETS } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';

import ActionsCell from './ActionsCell';
import {
  getDatasourceStatusText,
  getFlavour,
  getDatasourceHealth,
  getDatasourcesDocumentsTableIcon,
  isDocumentRow,
  getKbRowAction,
} from '../../../helper';
import { DatasourceDocument } from '../../../types';
import { ColumnAvatar } from '../ColumnAvatar';
import { FAQAnswer } from '../FAQAnswer';

export const columns = ({
  updateDocument,
  canWrite,
  category,
}): ColumnDef<DatasourceDocument>[] => {
  return [
    createSelectionColumn<DatasourceDocument>({
      getIcon: (row) => getDatasourcesDocumentsTableIcon(row)!,
      getLabel: (row) => row.name,
    }),
    {
      id: 'name',
      accessorKey: 'name',
      header: ({ column }) => (
        <SortableHeader column={column} className="pl-2">
          {t('collections.datasources.datasources')}
        </SortableHeader>
      ),
      cell: ({ row }) => {
        const document_count = row.original.document_count;
        const isFaqType = row.original.datasource_type === DatasourceType.FAQ;
        const isDocumentType = row.original.datasource_type === DatasourceType.FILES;
        const isGoogleDriveType = row.original.datasource_type === DatasourceType.GDRIVE;
        const isWebsiteType = row.original.datasource_type === DatasourceType.WEBSITE;
        const isExternalType =
          row.original.datasource_type === DatasourceType.ZENDESK_KB ||
          row.original.datasource_type === DatasourceType.INTERCOM_KB;

        const action = getKbRowAction(row.original, category);

        return (
          <div className="flex items-center gap-3 pl-2">
            <div className="flex min-w-0 flex-col">
              <span className="block truncate font-medium">{row.original.name}</span>

              {!!document_count && (
                <div className="text-xs text-foreground-muted">
                  {isFaqType && t('table.resource.faq', { count: document_count })}
                  {(isDocumentType || isGoogleDriveType) &&
                    t('table.resource.files', { count: document_count })}
                  {isWebsiteType && t('table.resource.url', { count: document_count })}
                  {isExternalType && t('table.resource.article', { count: document_count })}
                </div>
              )}

              {(isFaqType || (isDocumentType && row.original.type === 'txt')) && (
                <FAQAnswer
                  collection_id={row.original.collection_id}
                  datasource_id={row.original.datasource_id}
                  document_id={row.original.document_id}
                  preview={row.original.metadata?.preview as string | undefined}
                />
              )}
            </div>
            {action && <RowActionIndicator action={action} />}
          </div>
        );
      },
      ...COLUMN_PRESETS.FLEX,
      meta: { preset: 'FLEX' },
    },
    {
      id: 'status',
      accessorFn: getStatusSortValue,
      header: ({ column }) => (
        <SortableHeader column={column}>
          {category === 'webpages' ? t('collections.report.health') : t('common.status')}
        </SortableHeader>
      ),
      cell: ({ row }) => {
        const { is_draft, status } = row.original;
        const getStatusText = () => {
          return getDatasourceStatusText(status, is_draft, t);
        };

        // Datasource rows carry the crawl verdict, not a document status. Before the v2 report
        // this cell was blank for them, which is why a website never showed how indexing went.
        if (!isDocumentRow(row.original)) {
          const health = getDatasourceHealth(row.original, t);
          if (!health) {
            return null;
          }
          return (
            <Badge flavour={health.flavour} type="secondary" rounded={false}>
              {health.label}
            </Badge>
          );
        }

        const getTooltipContent = () => {
          if (status === 'available') {
            if (is_draft) {
              return t('collections.status_tooltip.draft');
            }
            return t('collections.status_tooltip.published');
          }
          return null;
        };

        const tooltipContent = getTooltipContent();

        if (!tooltipContent) {
          return (
            <Badge flavour={getFlavour(status, is_draft)} type="secondary" rounded={false}>
              {getStatusText()}
            </Badge>
          );
        }

        return (
          <InfoTooltip content={tooltipContent} side="top">
            <Badge flavour={getFlavour(status, is_draft)} type="secondary" rounded={false}>
              {getStatusText()}
            </Badge>
          </InfoTooltip>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD' },
    },
    {
      id: 'created_by',
      header: () => t('common.created_by'),
      cell: ({ row }) => {
        const createdById = row.original.created_by;

        return (
          <div className="pl-3">
            <ColumnAvatar createdById={createdById} />
          </div>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD' },
    },
    {
      accessorKey: 'updated',
      header: ({ column }) => (
        <SortableHeader column={column}>{t('common.updated')}</SortableHeader>
      ),
      cell: ({ row }) => {
        const updated = row.original.updated;
        return (
          <div>
            {updated ? (
              <TimestampTooltip timestamp={updated}>{timeFromNow(updated)}</TimestampTooltip>
            ) : (
              '-'
            )}
          </div>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD' },
    },
    {
      id: 'enable',
      header: () => t('common.active'),
      cell: (info) => {
        const row = info.row.original;
        if (!isDocumentRow(row)) {
          return (
            <span className="text-sm">
              {t('collections.details.out_of', {
                0: row.enabled_count ?? 0,
                1: row.document_count ?? 0,
              })}
            </span>
          );
        }

        return (
          <Switch
            checked={row.enabled}
            onCheckedChange={(enabled) => {
              updateDocument({ ...row, enabled });
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            disabled={!canWrite}
            id={`enable-${row.document_id || row.datasource_id}`}
          />
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD' },
    },
    {
      id: 'actions',
      header: () => '',
      cell: ({ row }) => {
        const item = row.original;
        return <ActionsCell item={item} />;
      },
      ...COLUMN_PRESETS.SM,
      meta: { preset: 'SM' },
    },
  ];
};
